// At top
import React, { useState, useEffect, useRef, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { ChevronDown, X, Plus, Minus, Search, RefreshCcw, Check, Calendar } from 'lucide-react';
import { CustomDropdown, formatToUSDate } from '../../hooks/commonFunction';

const customStyles = {
  rows: {
    style: {
      minHeight: '70px',
      textAlign: 'center',
    },
  },
  headCells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      minHeight: '70px',
      whiteSpace: 'nowrap',
      textAlign: 'center',
      justifyContent: 'center',
      fontWeight: 'medium',
      fontSize: '14px',
      backgroundColor: '#f8f8f8',
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      textAlign: 'center',
      justifyContent: 'center',
    },
  },
};

const ReusableDataTable = ({
  columns = [],
  data = [],
  filters = [],
  onFilterChange,
  visibleColumns = 3,
  showFilters,
  setShowFilters,
  showDateRangeFilter = false,
  showCreatedDateFilter = false,
  showCheckbox = true,
  selectedRows = [],
  onSelectedRowsChange,
  isLoading = false,
  isFilterLoading = false,
  defaultDateRange = {},
  defaultCreatedDateRange = {},
  initialFilters = {},
  paginationServer = false,
  paginationTotalRows = 0,
  paginationDefaultPage = 1,
  paginationRowsPerPage = 25,
  onChangePage,
  onChangeRowsPerPage,
  dateStartLabel = 'Delivery Start Date',
  dateEndLabel = 'Delivery End Date',
  createdDateStartLabel = 'Created Start Date',
  createdDateEndLabel = 'Created End Date',
  dateMin,    // optional: restrict delivery start/end date picker minimum date
  dateMax,    // optional: restrict delivery start/end date picker maximum date
}) => {
  const [filterState, setFilterState] = useState(initialFilters);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [tempFilterState, setTempFilterState] = useState(initialFilters);
  const [rowsPerPage, setRowsPerPage] = useState(paginationRowsPerPage);

  // Update rowsPerPage state if prop changes
  useEffect(() => {
    setRowsPerPage(paginationRowsPerPage);
  }, [paginationRowsPerPage]);

  // Refs for date inputs
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const createdStartDateRef = useRef(null);
  const createdEndDateRef = useRef(null);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return dateString;
  };

  // Seed tempFilterState with initialFilters when they arrive (e.g. restored from sessionStorage)
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      setTempFilterState((prev) => ({ ...prev, ...initialFilters }));
      setFilterState((prev) => ({ ...prev, ...initialFilters }));
    }
  }, [JSON.stringify(initialFilters)]);

  // Initialize tempFilterState with default date range.
  // Guard: only apply if the field is currently unset (undefined/'') —
  // don't override null (explicit reset) or a date the user already picked.
  useEffect(() => {
    if (defaultDateRange.startDate && defaultDateRange.endDate) {
      setTempFilterState((prev) => ({
        ...prev,
        startDate:
          prev.startDate === undefined || prev.startDate === ''
            ? defaultDateRange.startDate
            : prev.startDate,
        endDate:
          prev.endDate === undefined || prev.endDate === ''
            ? defaultDateRange.endDate
            : prev.endDate,
      }));
    }
  }, [defaultDateRange]);

  // Initialize tempFilterState with default created date range.
  // Guard: only apply if the field is currently unset (undefined/'') —
  // don't override null (explicit reset) or a date the user already picked.
  useEffect(() => {
    if (defaultCreatedDateRange.startDate || defaultCreatedDateRange.endDate) {
      setTempFilterState((prev) => ({
        ...prev,
        createdStartDate:
          prev.createdStartDate === undefined || prev.createdStartDate === ''
            ? defaultCreatedDateRange.startDate || ''
            : prev.createdStartDate,
        createdEndDate:
          prev.createdEndDate === undefined || prev.createdEndDate === ''
            ? defaultCreatedDateRange.endDate || ''
            : prev.createdEndDate,
      }));
    }
  }, [defaultCreatedDateRange]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleRow = (row) => {
    setExpandedRowIds((prev) =>
      prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id],
    );
  };

  const handleTempFilterChange = async (key, value) => {
    const updatedFilters = { ...tempFilterState, [key]: value };
    // Validate delivery dates
    if (key === 'startDate' && tempFilterState.endDate) {
      // If changing start date and end date exists, check if end is before new start
      if (new Date(value) > new Date(tempFilterState.endDate)) {
        // Reset end date if it's before the new start date
        updatedFilters.endDate = '';
      }
    }

    if (key === 'endDate' && tempFilterState.startDate) {
      // If changing end date, ensure it's not before start date
      if (new Date(value) < new Date(tempFilterState.startDate)) {
        // Don't update if end date is before start date
        //alert('End date cannot be earlier than start date');
        return;
      }
    }

    // Validate created dates
    if (key === 'createdStartDate' && tempFilterState.createdEndDate) {
      // If changing start date and end date exists, check if end is before new start
      if (new Date(value) > new Date(tempFilterState.createdEndDate)) {
        // Reset end date if it's before the new start date
        updatedFilters.createdEndDate = '';
      }
    }

    if (key === 'createdEndDate' && tempFilterState.createdStartDate) {
      // If changing end date, ensure it's not before start date
      if (new Date(value) < new Date(tempFilterState.createdStartDate)) {
        // Don't update if end date is before start date
        return;
      }
    }

    setTempFilterState(updatedFilters);
    setFilterState(updatedFilters);

    // Auto-apply filters
    if (onFilterChange) {
      try {
        await onFilterChange(updatedFilters);
      } catch (error) {
        console.error('Filter application failed:', error);
      }
    }
  };

  const applyFilters = () => {
    // Filters are now auto-applied, so this just closes the panel
    setShowFilters(false);
  };

  const resetFilters = async () => {
    setShowFilters(false);
    // Clear persisted filter state from localStorage
    try { localStorage.removeItem('ordersListState'); } catch (_) {}
    // Reset all filters to empty
    const resetState = {};

    // Reset all filter fields from the filters array
    filters.forEach((filter) => {
      resetState[filter.key] = '';
    });

    // Reset date filters
    // Use null (not '') so the API payload knows this was an explicit reset
    // (vs. '' which means "no user value yet" and triggers the qurbani default fallback)
    resetState.startDate = null;
    resetState.endDate = null;

    // Reset created date filters
    resetState.createdStartDate = null;
    resetState.createdEndDate = null;

    setTempFilterState(resetState);
    setFilterState(resetState);

    // Call parent filter function with reset state
    if (onFilterChange) {
      try {
        await onFilterChange(resetState);
        // Close filter panel after successful reset
        setShowFilters(false);
      } catch (error) {
        console.error('Filter reset failed:', error);
      }
    }
  };

  // Selection handlers
  const handleSelectAll = () => {
    const isAllSelected = selectedRows.length === data.length;
    const newSelectedRows = isAllSelected ? [] : data.map((row) => row.id || row);
    onSelectedRowsChange?.(newSelectedRows);
  };

  const handleSelectRow = (row) => {
    const rowId = row.id || row;
    const isSelected = selectedRows.includes(rowId);
    const newSelectedRows = isSelected
      ? selectedRows.filter((id) => id !== rowId)
      : [...selectedRows, rowId];
    onSelectedRowsChange?.(newSelectedRows);
  };

  const isAllSelected = data.length > 0 && selectedRows.length === data.length;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < data.length;

  const checkboxColumn = useMemo(() => {
    if (!showCheckbox) return [];

    return [
      {
        id: 'selection',
        name: (
          <div
            className='flex items-center justify-center'
            key={`header-${isAllSelected}-${selectedRows.length}`}
          >
            <label className='flex items-center justify-center cursor-pointer m-0'>
              <input
                key={`checkbox-header-${isAllSelected}-${isIndeterminate}`}
                type='checkbox'
                checked={isAllSelected}
                ref={(input) => {
                  if (input) input.indeterminate = isIndeterminate;
                }}
                onChange={handleSelectAll}
                className='form-checkbox cursor-pointer'
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#dc2626',
                  minWidth: '18px',
                  minHeight: '18px',
                  position: 'relative',
                  zIndex: 10,
                  margin: 0,
                  opacity: 1,
                  pointerEvents: 'auto',
                }}
              />
            </label>
          </div>
        ),
        cell: (row) => {
          const rowId = row.id || row;
          const isChecked = selectedRows.includes(rowId);
          return (
            <div onClick={(e) => e.stopPropagation()} className='flex items-center justify-center'>
              <label className='flex items-center justify-center cursor-pointer m-0'>
                <input
                  key={`checkbox-${rowId}-${isChecked}`}
                  type='checkbox'
                  checked={isChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectRow(row);
                  }}
                  className='form-checkbox cursor-pointer'
                  style={{
                    width: '18px',
                    height: '18px',
                    accentColor: '#dc2626',
                    minWidth: '18px',
                    minHeight: '18px',
                    position: 'relative',
                    zIndex: 10,
                    margin: 0,
                    opacity: 1,
                    pointerEvents: 'auto',
                  }}
                />
              </label>
            </div>
          );
        },
        width: '50px',
        ignoreRowClick: true,
        allowOverflow: true,
      },
    ];
  }, [
    showCheckbox,
    selectedRows,
    data.length,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectRow,
  ]);

  const mobileColumns = isMobile
    ? [
        ...checkboxColumn,
        ...columns.slice(0, visibleColumns),
        // Always include Action/button columns so edit/delete are visible on mobile
        ...columns.slice(visibleColumns).filter((col) => col.button === true || col.name === 'Action'),
      ]
    : [...checkboxColumn, ...columns];

  const expandableRows = isMobile;

  const expandableRowsComponent = ({ data }) => {
    // Get columns that are hidden in mobile view
    const hiddenColumns = columns.slice(visibleColumns);

    return (
      <div className='p-4 text-left bg-gray-50'>
        {hiddenColumns.map((column, index) => {
          // Get the value using column's selector or accessor
          let value;

          if (column.cell && typeof column.cell === 'function') {
            // If column has custom cell renderer, use it
            value = column.cell(data);
          } else if (column.selector && typeof column.selector === 'function') {
            // If column has selector function
            value = column.selector(data);
          } else if (column.selector && typeof column.selector === 'string') {
            // If selector is a string path
            value = data[column.selector];
          } else {
            // Fallback to empty
            value = '-';
          }

          // Handle React elements (JSX)
          if (React.isValidElement(value)) {
            return (
              <div
                key={index}
                className='text-sm last:border-b-0 border-b border-neutral-200 pb-2 mb-2 flex gap-2'
              >
                <b className='text-gray-700 w-[40%] flex-shrink-0 text-left'>{column.name}:</b>
                <div className='w-[60%] text-left'>{value}</div>
              </div>
            );
          }

          // Handle objects
          if (typeof value === 'object' && value !== null) {
            return null; // Skip objects that aren't React elements
          }

          return (
            <div
              key={index}
              className='text-sm last:border-b-0 border-b border-neutral-200 pb-2 mb-2 flex gap-2'
            >
              <b className='text-gray-700 w-[40%] flex-shrink-0 text-left'>{column.name}:</b>
              <span className='text-gray-900 w-[60%] text-left'>{value || '-'}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const filteredData = isMobile
    ? data.map((row) => ({
        ...row,
        isExpanded: expandedRowIds.includes(row.id),
      }))
    : data;

  return (
    <div className='w-full'>
      {/* Advanced Filters */}
      {showFilters && (
        <div className='bg-white shadow-primary rounded-lg p-5 mb-5 relative transition-all duration-300'>
          <button className='absolute top-2 right-2' onClick={() => setShowFilters(false)}>
            <X className='text-primary-500 h-5 w-5' />
          </button>
          <div className='grid grid-cols-2 md:grid-cols-5 gap-x-5 gap-4'>
            {/* Dropdown / Text Input filters */}
            {filters.length > 0 &&
              filters.map((f) => (
                <div key={f.key} className='w-full'>
                  <label className='text-gray-600 font-medium text-sm mb-1 block'>{f.label}</label>
                  {f.type === 'text' ? (
                    <input
                      type='text'
                      value={tempFilterState[f.key] ?? ''}
                      onChange={(e) => handleTempFilterChange(f.key, e.target.value)}
                      placeholder={f.placeholder || `Enter ${f.label}`}
                      className='w-full border py-2.5 shadow-sm text-sm focus:outline-none px-4 rounded-lg focus:ring-2 focus:ring-red-700 hover:border-gray-400 transition-colors h-[46px]'
                    />
                  ) : (
                    <CustomDropdown
                      value={tempFilterState[f.key] ?? ''}
                      onChange={(value) => handleTempFilterChange(f.key, value)}
                      options={f.options}
                      placeholder={f.options[0]?.label || `Select ${f.label}`}
                      isMulti={f.isMulti}
                    />
                  )}
                </div>
              ))}

            {/* Date Range Filters (each in its own grid cell) */}
            {showDateRangeFilter && (
              <>
                <div className='w-full'>
                  <label className='text-gray-600 font-medium text-sm'>{dateStartLabel}</label>
                  <div
                    className='relative cursor-pointer'
                    onClick={() => startDateRef.current?.showPicker?.()}
                  >
                    <input
                      type='text'
                      className='appearance-none w-full border py-3 shadow-sm text-sm focus:outline-none px-2 md:px-5 pr-10 rounded-lg cursor-pointer'
                      value={formatToUSDate(tempFilterState.startDate) || 'Select date'}
                      readOnly
                      placeholder='Select date'
                    />
                    <Calendar className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none' />
                    <input
                      ref={startDateRef}
                      type='date'
                      className='absolute inset-0 opacity-0 cursor-pointer w-full h-full'
                      value={formatDateForInput(tempFilterState.startDate) || ''}
                      min={dateMin || ''}
                      max={dateMax || ''}
                      onChange={(e) => handleTempFilterChange('startDate', e.target.value)}
                    />
                  </div>
                </div>
                <div className='w-full'>
                  <label className='text-gray-600 font-medium text-sm'>{dateEndLabel}</label>
                  <div
                    className='relative cursor-pointer'
                    onClick={() => endDateRef.current?.showPicker?.()}
                  >
                    <input
                      type='text'
                      className='appearance-none w-full border py-3 shadow-sm text-sm focus:outline-none px-2 md:px-5 pr-10 rounded-lg cursor-pointer'
                      value={formatToUSDate(tempFilterState.endDate) || 'Select date'}
                      readOnly
                      placeholder='Select date'
                    />
                    <Calendar className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none' />
                    <input
                      ref={endDateRef}
                      type='date'
                      className='absolute inset-0 opacity-0 cursor-pointer w-full h-full'
                      value={formatDateForInput(tempFilterState.endDate) || ''}
                      min={tempFilterState.startDate || dateMin || ''}
                      max={dateMax || ''}
                      onChange={(e) => handleTempFilterChange('endDate', e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Created Date Range Filters */}
            {showCreatedDateFilter && (
              <>
                <div className='w-full'>
                  <label className='text-gray-600 font-medium text-sm'>{createdDateStartLabel}</label>
                  <div
                    className='relative cursor-pointer'
                    onClick={() => createdStartDateRef.current?.showPicker?.()}
                  >
                    <input
                      type='text'
                      className='appearance-none w-full border py-3 shadow-sm text-sm focus:outline-none px-2 md:px-5 pr-10 rounded-lg cursor-pointer'
                      value={formatToUSDate(tempFilterState.createdStartDate) || 'Select date'}
                      readOnly
                      placeholder='Select date'
                    />
                    <Calendar className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none' />
                    <input
                      ref={createdStartDateRef}
                      type='date'
                      className='absolute inset-0 opacity-0 cursor-pointer w-full h-full'
                      value={formatDateForInput(tempFilterState.createdStartDate) || ''}
                      onChange={(e) => handleTempFilterChange('createdStartDate', e.target.value)}
                    />
                  </div>
                </div>
                <div className='w-full'>
                  <label className='text-gray-600 font-medium text-sm'>{createdDateEndLabel}</label>
                  <div
                    className='relative cursor-pointer'
                    onClick={() => createdEndDateRef.current?.showPicker?.()}
                  >
                    <input
                      type='text'
                      className='appearance-none w-full border py-3 shadow-sm text-sm focus:outline-none px-2 md:px-5 pr-10 rounded-lg cursor-pointer'
                      value={formatToUSDate(tempFilterState.createdEndDate) || 'Select date'}
                      readOnly
                      placeholder='Select date'
                    />
                    <Calendar className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none' />
                    <input
                      ref={createdEndDateRef}
                      type='date'
                      className='absolute inset-0 opacity-0 cursor-pointer w-full h-full'
                      value={formatDateForInput(tempFilterState.createdEndDate) || ''}
                      min={tempFilterState.createdStartDate || ''}
                      onChange={(e) => handleTempFilterChange('createdEndDate', e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className='flex justify-center md:justify-end pt-4 md:pt-0'>
            <button
              onClick={resetFilters}
              className='flex items-center gap-2 text-gray-400 letter-spacing-07 hover:text-primary-100/80 transition-colors font-medium text-sm underline'
            >
              <RefreshCcw className='h-4 w-4' />
              Reset Filter
            </button>
          </div>
        </div>
      )}
      {/* Table */}
      <div className='overflow-x-auto rounded-lg'>
        {isLoading || isFilterLoading ? (
          <div className='flex items-center justify-center h-full w-full min-h-[300px] bg-gray-50'>
            <div className='text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4'></div>
              <p className='text-gray-600 text-base'>
                {isFilterLoading ? 'Applying filters...' : 'Loading orders...'}
              </p>
            </div>
          </div>
        ) : data.length > 0 && columns.length > 0 ? (
          <DataTable
            key={`datatable-${paginationDefaultPage}-${selectedRows.length}-${isAllSelected}`}
            customStyles={customStyles}
            data={filteredData}
            columns={mobileColumns}
            striped={false}
            highlightOnHover
            pagination
            paginationServer={paginationServer}
            paginationTotalRows={paginationTotalRows}
            paginationDefaultPage={paginationDefaultPage}
            paginationResetDefaultPage={true}
            paginationPerPage={rowsPerPage}
            paginationRowsPerPageOptions={[25, 50, 100, 200, 500, 2000, 5000]}
            onChangePage={onChangePage}
            //onChangeRowsPerPage={onChangeRowsPerPage}
            onChangeRowsPerPage={(newPerPage, page) => {
              setRowsPerPage(newPerPage);
              onChangeRowsPerPage?.(newPerPage, page);
            }}
            responsive
            expandableRows={expandableRows}
            expandableRowExpanded={(row) => row.isExpanded}
            expandableRowsComponent={expandableRowsComponent}
            progressPending={isLoading || isFilterLoading}
          />
        ) : (
          <div className='flex items-center justify-center h-full w-full min-h-[200px] bg-gray-50'>
            <p className='text-center text-gray-500 text-base'>No data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReusableDataTable;